import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Instagram } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import LanguageSwitcher from './LanguageSwitcher';

const LOGO = 'https://customer-assets.emergentagent.com/job_9af72dbd-3207-4138-8995-8089c0baeb3e/artifacts/71v6xly5_ChatGPT%20Image%2029%20apr%202026%2C%2011_15_29.png';

export function Header() {
  const { t } = useI18n();
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  React.useEffect(() => { setMobile(false); }, [loc.pathname]);

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/catalog', label: t('nav.catalog') },
    { to: '/sell', label: t('nav.sell') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#08231a]/85 border-b border-[#c9a961]/15" data-testid="site-header">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 lg:px-12 h-20 lg:h-24">
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-3"
          data-testid="brand-logo-link"
        >
          <img src={LOGO} alt="GN Watches" className="h-12 w-12 object-cover" style={{ borderRadius: 2 }} />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-xl text-[#f5f5f0] tracking-wider">GN WATCHES</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#c9a961]/80 mt-0.5">Swiss Quality · Timeless Value</span>
          </div>
        </Link>

        <div className="flex items-center gap-5">
          <LanguageSwitcher />
          <button
            className="text-[#f5f5f0] hover:text-[#c9a961] transition-colors"
            onClick={() => setMobile((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="bg-[#05150f] border-t border-[#c9a961]/15" data-testid="mobile-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `block px-6 py-4 text-sm tracking-[0.18em] uppercase border-b border-[#c9a961]/10 ${
                  isActive ? 'text-[#c9a961]' : 'text-[#f5f5f0]'
                }`
              }
              data-testid={`mobile-nav-link-${l.to.replace('/', '') || 'home'}`}
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="bg-[#05150f] border-t border-[#c9a961]/20 mt-32 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          <img src={LOGO} alt="GN Watches" className="h-16 w-16 mb-4" style={{ borderRadius: 2 }} />
          <h3 className="font-serif text-2xl text-[#f5f5f0]">GN WATCHES</h3>
        </div>

        <div>
          <h4 className="gn-overline mb-5">Offices</h4>
          <div className="flex items-start gap-3 text-sm text-[#a3b5ad] mb-3">
            <MapPin size={16} className="mt-0.5 text-[#c9a961] flex-shrink-0" />
            <div>Via Pretorio 11<br/>6901 Lugano, CH</div>
          </div>
          <div className="flex items-start gap-3 text-sm text-[#a3b5ad]">
            <MapPin size={16} className="mt-0.5 text-[#c9a961] flex-shrink-0" />
            <div>Via Adriana Ramelli 1<br/>6900 Lugano, CH</div>
          </div>
        </div>

        <div>
          <h4 className="gn-overline mb-5">{t('footer.contact_h')}</h4>
          <a href="tel:+41782008081" className="flex items-center gap-3 text-sm text-[#a3b5ad] hover:text-[#c9a961] mb-3" data-testid="footer-phone-1">
            <Phone size={16} className="text-[#c9a961]" /> +41 78 200 8081
          </a>
          <a href="tel:+41792898008" className="flex items-center gap-3 text-sm text-[#a3b5ad] hover:text-[#c9a961] mb-3" data-testid="footer-phone-2">
            <Phone size={16} className="text-[#c9a961]" /> +41 79 289 8008
          </a>
          <a href="mailto:info@gnwatches.com" className="flex items-center gap-3 text-sm text-[#a3b5ad] hover:text-[#c9a961]" data-testid="footer-email">
            <Mail size={16} className="text-[#c9a961]" /> info@gnwatches.com
          </a>
        </div>

        <div>
          <h4 className="gn-overline mb-5">{t('footer.follow_us')}</h4>
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/gnwatches_"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-12 h-12 border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://www.tiktok.com/@gnwatches_"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="w-12 h-12 border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] hover:bg-[#c9a961] hover:text-[#05150f] transition-colors"
              data-testid="footer-tiktok"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7.04a4.85 4.85 0 0 1-1.84-.35z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 mt-16 pt-8 border-t border-[#c9a961]/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-xs text-[#a3b5ad] tracking-wider">© {new Date().getFullYear()} GN Watches. {t('footer.rights')}</p>
        <div className="flex items-center gap-6 text-xs tracking-wider">
          <Link to="/terms-conditions" className="text-[#a3b5ad] hover:text-[#c9a961] transition-colors" data-testid="footer-terms-link">{t('footer.terms')}</Link>
          <Link to="/privacy-policy" className="text-[#a3b5ad] hover:text-[#c9a961] transition-colors" data-testid="footer-privacy-link">{t('footer.privacy')}</Link>
          <Link to="/cookie-policy" className="text-[#a3b5ad] hover:text-[#c9a961] transition-colors" data-testid="footer-cookie-link">{t('footer.cookies')}</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20 lg:pt-24">{children}</main>
      <Footer />
    </div>
  );
}
